/**
 * Centralized query key factory for TanStack Query
 * Provides consistent and hierarchical query key structure
 */

import { format } from 'date-fns';
import { tz } from '@date-fns/tz';

const berlinTz = tz('Europe/Berlin');

const analyticsCalendarKey = (value) => {
	if (value instanceof Date) {
		return format(value, 'yyyy-MM-dd', { in: berlinTz });
	}

	return value;
};

const instantKey = (value) => (value instanceof Date ? value.toISOString() : value);

/**
 * Append a parameter object to a query key, but only with the parameters that are
 * actually set — and omit it entirely when none are.
 *
 * TanStack Query matches an invalidation filter against a cached key by partial deep
 * equality: every key present in the *filter* must be deeply equal in the cached key.
 * An explicit `undefined` is therefore NOT a wildcard — `{ date: undefined }` never
 * matches `{ date: '2026-08-13' }`, so calling a factory without its argument (which is
 * exactly how cacheStrategy.js builds its invalidation filters) would match nothing at
 * all. Dropping the unset parameters turns those calls into the intended prefix filter.
 *
 * @param {Array} baseKey - Key prefix
 * @param {Object} params - Parameters, possibly undefined
 * @returns {Array} Query key
 */
const withParams = (baseKey, params) => {
	const definedParams = Object.fromEntries(
		Object.entries(params).filter(([, value]) => value !== undefined),
	);

	return Object.keys(definedParams).length > 0
		? [...baseKey, definedParams]
		: baseKey;
};

export const queryKeys = {
	// Base keys
	all: ["room-booking-management"],

	// Booking-related queries
	bookings: () => [...queryKeys.all, "bookings"],
	bookingsByRoom: (roomId, dateRange) =>
		withParams([...queryKeys.bookings(), "by-room"], { roomId, dateRange }),
	allBookings: (date) => withParams([...queryKeys.bookings(), "all"], { date }),
	myBookings: (dateRange) =>
		withParams([...queryKeys.bookings(), "my-bookings"], { dateRange }),
	bookingLogs: (bookingId) =>
		withParams([...queryKeys.bookings(), "logs"], { bookingId }),
	// Prefix of bookingLogs() with no bookingId filter — invalidating this matches every
	// already-cached per-booking log query, regardless of which booking. Needed for any
	// operation that writes a series-level log entry, since that entry is visible from any
	// instance's log view (not just the one the operation was performed on), and we don't
	// always know in advance which specific booking IDs are currently cached/viewed.
	allBookingLogs: () => [...queryKeys.bookings(), "logs"],

	// Master data queries
	masterData: () => [...queryKeys.all, "master-data"],
	masterDataFull: () => [...queryKeys.masterData(), "full"],

	// User-related queries
	users: () => [...queryKeys.all, "users"],
	userSearch: (query) => [...queryKeys.users(), "search", { query }],
	myTransactions: () => [...queryKeys.users(), "my-transactions"],

	// Building-related queries
	buildings: () => [...queryKeys.all, "buildings"],
	buildingOpeningHours: (buildingId) => [
		...queryKeys.buildings(),
		"opening-hours",
		{ buildingId },
	],

	// Room-related queries
	rooms: () => [...queryKeys.all, "rooms"],
	roomsList: () => [...queryKeys.rooms(), "list"],

	// Concert request prefill
	concertRequestPrefill: (id) => [
		...queryKeys.all,
		"concert-request-prefill",
		{ id },
	],

	// Admin-related queries
	admin: () => [...queryKeys.all, "admin"],
	adminLogs: (filters) =>
		filters === undefined
			? [...queryKeys.admin(), "logs"]
			: [...queryKeys.admin(), "logs", filters],
	adminRooms: () => [...queryKeys.admin(), "rooms"],

	account: () => [...queryKeys.all, 'account'],
	accountStudents: () => [...queryKeys.account(), 'students'],
	accountEmployees: () => [...queryKeys.account(), 'employees'],
	accountAnalytics: ({ from, until } = {}) => [
		...queryKeys.account(),
		'analytics',
		{
			from: analyticsCalendarKey(from),
			until: analyticsCalendarKey(until),
		}
	],
	accountUserBookings: ({ studentId, employeeId, from, until } = {}) => [
		...queryKeys.account(),
		'bookings',
		{
			studentId,
			employeeId,
			from: instantKey(from),
			until: instantKey(until),
		}
	],
	accountStudentDetails: ({ studentId } = {}) => [
		...queryKeys.account(),
		'student-details',
		{ studentId }
	],

	// Series-related queries
	series: () => [...queryKeys.all, "series"],
	seriesById: (seriesId) => [...queryKeys.series(), "detail", seriesId],
};

/**
 * Utility functions for query invalidation
 */
export const invalidateQueries = {
	// Invalidate all booking-related queries
	allBookings: () => queryKeys.bookings(),

	// Invalidate bookings for a specific room
	roomBookings: (roomId) => queryKeys.bookingsByRoom(roomId),

	// Invalidate all bookings for a specific date
	bookingsByDate: (date) => queryKeys.allBookings(date),

	// Invalidate user's personal bookings
	myBookings: (dateRange) => queryKeys.myBookings(dateRange),

	// Invalidate booking logs
	bookingLogs: (bookingId) => queryKeys.bookingLogs(bookingId),

	// Invalidate series-related queries
	allSeries: () => queryKeys.series(),
	seriesById: (seriesId) => queryKeys.seriesById(seriesId),

	// Invalidate all queries (use with caution)
	all: () => queryKeys.all,

	// Invalidate master data
	masterData: () => queryKeys.masterData(),

	// Invalidate admin logs
	adminLogs: () => [...queryKeys.admin(), "logs"],

	// Invalidate room-related queries
	roomsList: () => queryKeys.roomsList(),

	// Invalidate user search queries
	userSearch: (query) => queryKeys.userSearch(query),

	// Invalidate user transactions
	myTransactions: () => queryKeys.myTransactions(),

	// Invalidate building opening hours
	buildingOpeningHours: (buildingId) =>
		queryKeys.buildingOpeningHours(buildingId),
};

/**
 * Query key validation helpers
 */
export const validateQueryKey = (key) => {
	if (!Array.isArray(key)) {
		throw new Error("Query key must be an array");
	}

	if (key.length === 0) {
		throw new Error("Query key cannot be empty");
	}

	if (key[0] !== "room-booking-management") {
		throw new Error('Query key must start with "room-booking-management"');
	}

	return true;
};
